import { useCallback, useMemo } from 'react'
import { matchPath, useLocation, useResolvedPath } from 'react-router-dom'

import { Tab } from '@ui/components/Tabs'

type Options = {
  includeNested?: boolean
  initialIndex?: number
}

export function useActiveTab<T = string>(tabs: readonly Tab<T>[], opts: Options = {}) {
  const { includeNested = true, initialIndex = 0 } = opts
  const location = useLocation()
  const base = useResolvedPath('.').pathname.replace(/\/$/, '')

  const findIndexFromLocation = useCallback(
    (pathname: string) => {
      if (!tabs.length) return 0

      const clamp = (n: number) => Math.min(Math.max(0, n), Math.max(0, tabs.length - 1))

      const getPath = (t: Tab<T>) =>
        typeof t.path === 'string' ? t.path : (t.path as any)?.pathname ?? String(t.path)

      let idx = tabs.findIndex((t) => {
        const p = getPath(t)
        return p.startsWith('/')
          ? !!matchPath({ path: p, end: true }, pathname)
          : !!matchPath({ path: `${base}/${p}`, end: true }, pathname)
      })
      if (idx !== -1) return idx

      if (includeNested) {
        idx = tabs.findIndex((t) => {
          const p = getPath(t)
          return p.startsWith('/')
            ? !!matchPath({ path: `${p}/*`, end: false }, pathname)
            : !!matchPath({ path: `${base}/${p}/*`, end: false }, pathname)
        })
        if (idx !== -1) return idx
      }

      return clamp(initialIndex)
    },
    [tabs, base, includeNested, initialIndex]
  )

  const activeIndex = useMemo(
    () => findIndexFromLocation(location.pathname),
    [location.pathname, findIndexFromLocation]
  )

  const activeTab = tabs[activeIndex]
  const activePath =
    typeof activeTab?.path === 'string' ? activeTab.path : (activeTab?.path as any)?.pathname ?? ''

  return { activeIndex, activePath, activeTab }
}
